import { useState, useEffect, useMemo } from "react";
import { difference } from "lodash";
import { West, East } from "@mui/icons-material";
import { Box, Paper, TextField, Button, Modal, Typography } from "@mui/material";
import { ChartKey, CustomTab } from "../../models/customTabs";
import ChartList from "./ChartList";
import { formatCustomTabName, CommercialChartCardTable, MedInfoChartCardTable } from "./common";
import useUserContext from "../Authorization/useUserContext";
import { titleCase } from "../../utils/utils";

interface ModalProps {
  open: boolean;
  onApply: (state: CustomTab) => void;
  onCancel: () => void;
  onDelete: () => void;
  initialState: CustomTab;
}

export default function EditModal({
  open,
  initialState,
  onApply: handleApply,
  onCancel: handleCancel,
  onDelete: handleDelete,
}: ModalProps) {
  const [chartKeys, setChartKeys] = useState<ChartKey[]>([]);
  const [name, setName] = useState(initialState.name);
  const [selectedLeft, setSelectedLeft] = useState<Set<ChartKey>>(new Set());
  const [selectedRight, setSelectedRight] = useState<Set<ChartKey>>(new Set());
  const user = useUserContext();
  const isUpdate = useMemo(() => initialState.name !== "", [open]);

  const charts = user.selectedRoles?.includes("commercial")
    ? CommercialChartCardTable
    : user.selectedRoles?.includes("med_info")
      ? MedInfoChartCardTable
      : CommercialChartCardTable;

  useEffect(() => {
    if (open) {
      setChartKeys(initialState.charts.map((c) => c.chart_type));
      setName(initialState.name);
    }
  }, [open, initialState]);

  const displayName = useMemo(() => {
    return formatCustomTabName(name || initialState.name) || "New Tab";
  }, [name, initialState.name]);

  const leftList = useMemo(() => difference(Object.keys(charts), chartKeys), [charts, chartKeys]);

  const rightList = useMemo(() => chartKeys, [chartKeys]);

  const MemoizedChartListLeft = useMemo(
    () => <ChartList title="Common Charts" selected={selectedLeft} onClick={setSelectedLeft} list={leftList} />,
    [selectedLeft, setSelectedLeft, leftList],
  );

  const MemoizedChartListRight = useMemo(
    () => <ChartList title="Selected Charts" selected={selectedRight} onClick={setSelectedRight} list={rightList} />,
    [selectedRight, setSelectedRight, rightList],
  );
  if (!open) {
    return null;
  }
  return (
    <Modal open={open}>
      <Box
        onSubmit={(e) => e.preventDefault()}
        component="form"
        display="flex"
        alignItems="center"
        justifyContent="center"
        height="100%"
      >
        <Paper
          sx={{
            "& p,input::placeholder": { letterSpacing: ".025em" },
            borderRadius: "1rem",
            maxHeight: "47rem",
            height: "90%",
            px: 4,
            pb: 3.5,
            display: "flex",
            flexDirection: "column",
            gap: 3.5,
            overflow: "hidden",
          }}
        >
          <Box
            display="flex"
            height="3.5rem"
            justifyContent="center"
            alignItems="center"
            borderBottom="1px solid rgba(0,0,0,0.1)"
            flexShrink={0}
          >
            <Typography fontWeight="bold" fontSize="1.25rem" color="rgba(0,0,0,0.8)">
              {displayName}
            </Typography>
          </Box>
          <TextField
            onChange={({ target: { value } }) => setName(titleCase(value))}
            value={name}
            placeholder="Enter name here (required)…"
            sx={{ "& .MuiInputBase-input": { py: 1.5 }, "& .MuiFormLabel-root": { mt: "-0.0rem" } }}
          />
          <Box display="flex" overflow="hidden" flexGrow={1} gap=".875rem">
            {MemoizedChartListLeft}
            <Box
              gap={1.5}
              flexGrow={1}
              justifyContent="center"
              alignItems="center"
              display="flex"
              flexDirection="column"
            >
              <Button
                onClick={() => {
                  setChartKeys([...chartKeys, ...selectedLeft]);
                  setSelectedLeft(new Set());
                }}
                disabled={selectedLeft.size === 0}
                sx={{
                  background: "rgba(217, 217, 217, 0.15)",
                  color: "rgba(0,0,0,0.6)",
                  justifyContent: "center",
                  minWidth: "3.5rem",
                }}
                size="small"
                variant="contained"
              >
                <East />
              </Button>
              <Button
                onClick={() => {
                  setChartKeys(difference(chartKeys, [...selectedRight]));
                  setSelectedRight(new Set());
                }}
                disabled={selectedRight.size === 0}
                sx={{
                  background: "rgba(217, 217, 217, 0.15)",
                  color: "rgba(0,0,0,0.6)",
                  justifyContent: "center",
                  minWidth: "3.5rem",
                }}
                size="small"
                variant="contained"
              >
                <West />
              </Button>
            </Box>
            {MemoizedChartListRight}
          </Box>
          <Box display="flex" gap={1.5} justifyContent="center" alignItems="center">
            {/* Not part of figma docs so let's ask Julie where to put delete button */}
            {name !== "" && (
              <Button
                onClick={handleDelete}
                sx={{
                  "&:hover": { color: "#C8102E", outlineColor: "#C8102E" },
                  color: "#5A5B5D",
                  background: "rgba(217, 217, 217, 0.25)",
                }}
                variant="contained"
              >
                Delete Tab
              </Button>
            )}
            <Button
              onClick={handleCancel}
              sx={{ color: "#5A5B5D", background: "rgba(217, 217, 217, 0.25)" }}
              variant="contained"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              color="secondary"
              sx={{ color: "#0B41CD", background: "rgba(11,65,205,0.15)" }}
              disabled={chartKeys.length === 0 || name === ""}
              onClick={() => {
                handleApply({ name: name, charts: chartKeys.map((_key) => ({ chart_type: _key })) });
              }}
              variant="contained"
            >
              {isUpdate ? "Update" : "Create"}
            </Button>
          </Box>
        </Paper>
      </Box>
    </Modal>
  );
}
